// Parity P5: the automations' and routines' queries and their cache keys (one place, so every
// write refreshes what shows it).
import type { OperationOutput } from "@companyos/sdk";
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export type Rule = OperationOutput<"automations.get">;
export type Proposal = OperationOutput<"automations.list">["proposals"][number];
export type Run = OperationOutput<"automations.runs">["data"][number];
export type Routine = OperationOutput<"seats.routines">["data"][number];
export type RoutineRun = OperationOutput<"recurringWork.history">["data"][number];
export type Draft = OperationOutput<"automations.draft">;

export const processKeys = {
  rules: (co: string) => ["company", co, "automations"] as const,
  runs: (co: string, id: string) => ["company", co, "automations", id, "runs"] as const,
  routines: (co: string, seat: string) => ["company", co, "routines", seat] as const,
  allRoutines: (co: string) => ["company", co, "routines"] as const,
  history: (co: string, id: string) => ["company", co, "routine-history", id] as const,
};

export const rulesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: processKeys.rules(co),
    queryFn: () => api.client.automations.list({ params: { co } }),
  });

export const runsQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: processKeys.runs(co, id),
    queryFn: () => api.client.automations.runs({ params: { co, id }, query: { limit: 50 } }),
  });

export const routinesQuery = (api: Api, co: string, seat: string) =>
  queryOptions({
    queryKey: processKeys.routines(co, seat),
    queryFn: () => api.client.seats.routines({ params: { co, id: seat } }),
  });

export const routineHistoryQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: processKeys.history(co, id),
    queryFn: () => api.client.recurringWork.history({ params: { co, id } }),
  });

/** The cadences the Repeat… and Add routine forms offer (RRULEs at 09:00, company time). */
export const CADENCES = [
  { id: "daily", label: "Every day", rule: "FREQ=DAILY;BYHOUR=9" },
  // daily on weekdays (not weekly with five days): the occurrence's task is keyed by the rule's
  // period, so a weekly rule would make one task a week
  { id: "weekdays", label: "Every weekday", rule: "FREQ=DAILY;BYDAY=MO,TU,WE,TH,FR;BYHOUR=9" },
  { id: "weekly", label: "Every week (Monday)", rule: "FREQ=WEEKLY;BYDAY=MO;BYHOUR=9" },
  { id: "biweekly", label: "Every two weeks", rule: "FREQ=WEEKLY;INTERVAL=2;BYDAY=MO;BYHOUR=9" },
  { id: "monthly", label: "Every month (the 1st)", rule: "FREQ=MONTHLY;BYMONTHDAY=1;BYHOUR=9" },
  { id: "after", label: "Days after the last one is done", rule: null },
] as const;
export type CadenceId = (typeof CADENCES)[number]["id"];

/** The body part a cadence choice sends. */
export function cadenceBody(
  id: CadenceId,
  days: number,
): { schedule: string } | { after_completion_days: number } {
  const c = CADENCES.find((x) => x.id === id);
  return c?.rule
    ? { schedule: c.rule }
    : { after_completion_days: Math.min(365, Math.max(1, days)) };
}
